/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import exam from '../../../../share/util/inputValidator';

/** 各フェーズ定義 */
const PHASE = {
  CONFIRM: 'confirm',
  INPUT_ID: 'input_id',
  SELECT: 'select',
  SUCCESS: 'success',
  FAILED: 'failed'
};


export default {
  name: 'requirement40003',
  data() {
    return {
      phase: PHASE.CONFIRM,
      isLoading: false,
      isFiveIds: false,
      inputIds: [],
      systemId: '',
      modal_requirement_40003: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.CONFIRM:
        case PHASE.INPUT_ID:
        case PHASE.SELECT:
        default:
          return 'secondary';
      }
    },

    /** ヘッダータイトル */
    headerTitle() {
      // 実行確認, 送信機IDの入力, 登録先の選択
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 正常終了
      const SuccessTitle = 'message.common_title_success';
      // 共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        confirm: ConfirmationTitle,
        input_id: ConfirmationTitle,
        select: ConfirmationTitle,
        success: SuccessTitle,
        failed: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /** ボディのメッセージ文言 */
    workSupportMessage() {
      const messageMap = {
        confirm: `${this.i18nWsKey}.REQ_WS_40003_TXT_1`,
        select: `${this.i18nWsKey}.REQ_WS_40003_TXT_4`,
        success: `${this.i18nWsKey}.REQ_WS_40003_TXT_5`,
        failed: `individual.error_communication_error`
      };
      return this.$t(messageMap[this.phase]);
    },

    /** OKボタンを表示するかどうか */
    showOKButton() {
      const showOKButtonMap = {
        confirm: true,
        input_id: true,
        select: false,
        success: true,
        failed: true
      };
      return showOKButtonMap[this.phase];
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_40003 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_40003 = false;
    },

    /** 登録本数読出し */
    async read() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了：読み出しデータ表示
              this.isFiveIds = response.data.user_data.is_five_ids;
              this.createInputIds();
              this.phase = PHASE.INPUT_ID;
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // 共通エラーに遷移
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 送信機ID書込み
     * @param {boolean} isMain 送信先がメインか否か
     */
    async write(isMain) {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'write',
            is_main: isMain,
            is_five_ids: this.isFiveIds,
            ids: this.inputIds.map((input) => input.id.toUpperCase())
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了：送信機ID書込み
              this.phase = PHASE.SUCCESS;
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // 共通エラーに遷移
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** Dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'dispose',
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        // 結果にかかわらず、Wizardを閉じる
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** OKボタン押下時の処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM:
          this.read();
          break;
        case PHASE.INPUT_ID:
          this.validateIds();
          break;
        case PHASE.SUCCESS:
        case PHASE.FAILED:
          this.dispose();
          break;
        default:
      }
    },

    /** 入力用ID配列を作成する */
    createInputIds() {
      const arrayCount = this.isFiveIds ? 5 : 4;
      for (let i = 0; i < arrayCount; i++) {
        const inputInfo = {
          id: '',
          valid: null,
          validMsg: '',
        };
        this.inputIds.push(inputInfo);
      }
    },

    /** IDのバリデーションを行う */
    validateIds() {
      // 16進表現に使用する半角英数字以外の場合は弾くパターン
      const halfReqExp = new RegExp(/^[0-9a-fA-F]*$/);
      this.inputIds.forEach((idInfo) => {
        const validResult = exam.excuteValidateCommons(idInfo.id, true, 7, 7, halfReqExp);
        idInfo.valid = validResult.valid;
        idInfo.validMsg = validResult.length ? this.$t(validResult.msg, ['7']) : this.$t(validResult.msg);
      });
      // すべてのIDが有効な場合、登録先選択画面へ遷移
      if (this.inputIds.every(result => result.valid === null)) {
        this.phase = PHASE.SELECT;
      }
    },
  },

  async mounted() {
    this.init();
  },
};